import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import {
  Lock, Mail, CalendarClock, LifeBuoy, MessageSquare, ShieldCheck,
  FolderKanban, GraduationCap, School, ListChecks, AlarmClock,
} from "lucide-react";

const LOGO = "/brand/wattline-logo-light.svg";

/**
 * Suite moduli mostrati nella vetrina destra del Login (PARTE C).
 * `active`: true = modulo già disponibile nella piattaforma; false = "In arrivo".
 * Ordine deliberato: prima le funzioni operative core, poi le orizzontali,
 * infine gli "in arrivo".
 */
const SUITE_MODULES = [
  { key: "presenze",   icon: CalendarClock, name: "Presenze e HR",         line: "Timbrature, ferie, permessi, malattia",   active: true  },
  { key: "ticket",     icon: LifeBuoy,      name: "Ticket e supporto",     line: "Comunicazione col supporto Wattline",     active: true  },
  { key: "messaggi",   icon: MessageSquare, name: "Messaggi interni",      line: "Chat privata tra colleghi dell'azienda",    active: true  },
  { key: "verifica",   icon: ShieldCheck,   name: "Verifica dati aziendali", line: "VIES, CCIAA, Catasto — fonti ufficiali", active: true  },
  { key: "formazione", icon: GraduationCap, name: "Formazione",            line: "Corsi di formazione con progressi tracciati",   active: true  },
  { key: "documenti",  icon: FolderKanban,  name: "Documenti",             line: "Archivio documentale aziendale",          active: false },
  { key: "checklist",  icon: ListChecks,    name: "Checklist",             line: "Liste personali e condivise, spunta in tempo reale", active: true  },
  { key: "scadenze",   icon: AlarmClock,    name: "Scadenze",              line: "Alert su rinnovi, contratti, adempimenti", active: false },
  { key: "onboarding", icon: School,        name: "Scuola onboarding",     line: "Percorsi di ingresso per nuove risorse",  active: true, badge: "Novità" },
];

export default function Login() {
  usePageMeta({
    title: "Accedi | Wattline",
    description: "Accedi alla tua area Wattline: presenze, documenti, verifiche imprese e assistente AI per il tuo team.",
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    // Normalizziamo l'email (lower + trim) per evitare problemi mobile:
    // - iOS Safari capitalizza la prima lettera (auto-capitalize)
    // - Autofill può aggiungere spazi accidentali
    // Password: rimuoviamo SOLO spazi all'inizio/fine (eventuali spazi interni
    // intenzionali restano intatti). I password manager mobile aggiungono spesso
    // un trailing whitespace dall'autofill.
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").replace(/^\s+|\s+$/g, "");
    try {
      const u = await login(cleanEmail, cleanPassword);
      const dest = u.must_change_password
        ? "/change-password"
        : (location.state?.from?.pathname || "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-page" data-testid="login-page">
      {/* Left: form */}
      <div className="w-full lg:w-[480px] flex flex-col px-8 sm:px-12 py-10 bg-surface border-r border-border">
        <div className="mb-12">
          <img src={LOGO} alt="Wattline" className="h-8 w-auto" data-testid="login-logo" />
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
          <div>
            <div
              className="text-[11px] font-medium tracking-widest uppercase mb-3"
              style={{color: "var(--wl-text-secondary)"}}
            >
              La piattaforma per il tuo back-office
            </div>
            <h1
              className="text-3xl tracking-tight"
              style={{
                fontFamily: "var(--wl-font-display)",
                fontWeight: "var(--wl-title-weight)",
                color: "var(--wl-ink)",
                letterSpacing: "var(--wl-title-track)",
              }}
            >
              Accedi al tuo account
            </h1>
            <p className="text-sm mt-2 leading-relaxed" style={{color: "var(--wl-text-secondary)"}}>
              Inserisci le credenziali fornite dall&apos;amministratore della tua organizzazione.
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="wl-field">
              <label htmlFor="email" className="wl-label wl-label--required">Email</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{color: "var(--wl-text-muted)"}} />
                <input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                  spellCheck={false}
                  inputMode="email"
                  className="wl-input"
                  style={{paddingLeft: "36px"}}
                  placeholder="es. mario.rossi@azienda.it"
                />
              </div>
            </div>

            <div className="wl-field">
              <label htmlFor="password" className="wl-label wl-label--required">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{color: "var(--wl-text-muted)"}} />
                <input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="current-password"
                  spellCheck={false}
                  className="wl-input"
                  style={{paddingLeft: "36px"}}
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="wl-error-msg"
                role="alert"
                style={{
                  background: "var(--wl-danger-bg)",
                  border: "1px solid var(--wl-danger)",
                  borderRadius: "var(--wl-rounded-control)",
                  padding: "10px 12px",
                  display: "block",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="wl-btn wl-btn--primary w-full"
              style={{height: "44px"}}
            >
              {submitting ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>

          <p className="mt-6 text-sm text-center" style={{color: "var(--wl-text-secondary)"}}>
            Nuovo su Wattline?{" "}
            <a href="/plans" className="wl-highlight" style={{textDecoration: "underline"}} data-testid="login-plans-link">
              Scopri i piani e prova gratis
            </a>
          </p>

          <p className="mt-10 text-xs" style={{color: "var(--wl-text-muted)"}}>
            © {new Date().getFullYear()} Wattline · Tutti i diritti riservati
          </p>
        </div>
      </div>

      {/* Right: suite showcase (desktop only) */}
      <div className="wl-login-showcase" data-testid="login-showcase">
        <div className="wl-login-showcase__inner">
          <h2 className="wl-login-showcase__headline">
            Il <span className="wl-highlight">back-office</span> della tua azienda,
            in <span className="wl-highlight">un&apos;unica piattaforma</span>.
          </h2>
          <p className="wl-login-showcase__lede">
            Persone, dati, comunicazione, formazione, controlli aziendali.
            Una sola piattaforma, un solo login, un solo posto dove tutto
            resta a portata di mano.
          </p>

          <div className="wl-suite-grid" data-testid="login-suite-grid">
            {SUITE_MODULES.map((m) => (
              <SuiteCard key={m.key} module={m} />
            ))}
          </div>

          <p className="wl-login-showcase__closing" data-testid="login-suite-closing">
            Ogni azienda attiva solo i moduli che le servono: personalizziamo
            la piattaforma in base alle tue esigenze e al tuo settore.
          </p>
        </div>
      </div>
    </div>
  );
}

const SuiteCard = ({ module: m }) => {
  const Icon = m.icon;
  return (
    <div
      className="wl-suite-card"
      data-active={m.active ? "true" : "false"}
      data-testid={`login-suite-card-${m.key}`}
    >
      <div className="wl-suite-card__icon" aria-hidden="true">
        <Icon size={16} strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="wl-suite-card__name">
          {m.name}
          {m.badge ? (
            <span className="wl-badge wl-badge--warning wl-suite-card__chip">{m.badge}</span>
          ) : !m.active && (
            <span className="wl-badge wl-badge--warning wl-suite-card__chip">Novità</span>
          )}
        </div>
        <div className="wl-suite-card__line">{m.line}</div>
      </div>
    </div>
  );
};
